import React, { useState } from 'react';
import { Box } from 'minimal-ui';
import ProductCard from './ProductCard';
import { getPromotionLineStockCap } from '../utils/promotionLineStock';
import { resolvePromotionProductMaxAllowedQty } from '../../common/maximumOrderQuantity';
import { resolveMandatoryGetMinQuantity } from '../utils/promotionOfferProductsShared';

const ProductCardWrapper = ({ 
    product, 
    tabType, 
    initialPricingType,
    productQuantities,
    existingCartQuantities,
    cartItems = [],
    promotionDetails = null,
    onQuantityChange,
    onQuantityLimitExceeded,
    onPricingTypeChange,
    onAddToCart,
    hasOtherPromotionsInCart
}) => {
    const [currentPricingType, setCurrentPricingType] = useState(initialPricingType);
    
    const uniqueKey = `${tabType}_${product.product_id}_${currentPricingType}`;
    const existingCartQty = existingCartQuantities[uniqueKey] || 0;
    const stockCapRaw = getPromotionLineStockCap(product, currentPricingType);
    const stockQty =
        stockCapRaw != null && Number.isFinite(stockCapRaw)
            ? Math.max(0, Math.floor(stockCapRaw))
            : null;
    const maxQuantityAllowed =
        tabType === 'get'
            ? null
            : resolvePromotionProductMaxAllowedQty(
                  product,
                  cartItems,
                  currentPricingType,
                  getPromotionLineStockCap
              );
    const rawQty =
        productQuantities[uniqueKey] !== undefined && productQuantities[uniqueKey] !== null
            ? Number(productQuantities[uniqueKey])
            : Number(existingCartQty) || 0;
    const displayQty =
        maxQuantityAllowed !== null ? Math.min(rawQty, maxQuantityAllowed) : rawQty;
    const compulsoryMinQuantity =
        tabType === 'get' && promotionDetails
            ? resolveMandatoryGetMinQuantity(
                  product,
                  promotionDetails,
                  existingCartQuantities,
                  productQuantities,
                  {
                      cartItems,
                      promotionIdStr: String(
                          promotionDetails?.id ?? promotionDetails?.promotion_id ?? ''
                      ),
                  }
              )
            : null;

    return (
        <ProductCard
            product={product}
            onQuantityChange={onQuantityChange}
            onQuantityLimitExceeded={onQuantityLimitExceeded}
            quantity={displayQty}
            maxQuantityFromStock={maxQuantityAllowed}
            stockQty={stockQty}
            tabType={tabType}
            pricingType={currentPricingType}
            setPricingType={(type) => {
                setCurrentPricingType(type);
                const newUniqueKey = `${tabType}_${product.product_id}_${type}`;
                onPricingTypeChange(newUniqueKey, type);
            }}
            onAddToCart={onAddToCart}
            existingCartQuantity={existingCartQty}
            hasOtherPromotionsInCart={hasOtherPromotionsInCart}
            compulsoryMinQuantity={compulsoryMinQuantity}
        />
    );
};

const ProductList = ({
    products,
    tabType,
    productQuantities,
    existingCartQuantities,
    productPricingTypes,
    cartItems = [],
    promotionDetails = null,
    onQuantityChange,
    onQuantityLimitExceeded,
    onPricingTypeChange,
    onAddToCart,
    hasOtherPromotionsInCart
}) => {
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {products.map((product) => {
                const productType = product?.product_type;
                const split = parseInt(product?.split || 1, 10);
                const boxKey = `${tabType}_${product.product_id}_Box`;
                const pieceKey = `${tabType}_${product.product_id}_Piece`;
                
                const hasBoxInCart = existingCartQuantities[boxKey] > 0;
                const hasPieceInCart = existingCartQuantities[pieceKey] > 0;
                
                let defaultPricingType = 'Box';
                if (productType === 'Piece' || split === 1) {
                    defaultPricingType = 'Piece';
                } else if (productType === 'Box') {
                    defaultPricingType = 'Box';
                } else if (productType === 'Both') {
                    defaultPricingType = 'Box';
                }
                
                const initialPricingType = hasBoxInCart ? 'Box' : hasPieceInCart ? 'Piece' : defaultPricingType;
                
                return (
                    <ProductCardWrapper
                        key={`${product.product_id}`}
                        product={product}
                        tabType={tabType}
                        initialPricingType={initialPricingType}
                        productQuantities={productQuantities}
                        existingCartQuantities={existingCartQuantities}
                        cartItems={cartItems}
                        promotionDetails={promotionDetails}
                        onQuantityChange={onQuantityChange}
                        onQuantityLimitExceeded={onQuantityLimitExceeded}
                        onPricingTypeChange={onPricingTypeChange}
                        onAddToCart={onAddToCart}
                        hasOtherPromotionsInCart={hasOtherPromotionsInCart}
                    />
                );
            })}
        </Box>
    );
};

export default ProductList;

